import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { z } from "zod";
import { AccountUnknown } from "./routes/AccountUnknown";
import { BevoegdhedenPage } from "./routes/BevoegdhedenPage";
import { FunctiesPage } from "./routes/FunctiesPage";
import { HandleidingPage } from "./routes/HandleidingPage";
import { KlassenPage } from "./routes/KlassenPage";
import { NiveausPage } from "./routes/NiveausPage";
import { SpeelschemasPage } from "./routes/SpeelschemasPage";
import { InschrijfperiodesPage } from "./routes/InschrijfperiodesPage";
import { LokaleAccountsPage } from "./routes/LokaleAccountsPage";
import { NotFound } from "./routes/NotFound";
import { OndersteunersPage } from "./routes/OndersteunersPage";
import { OverzichtPage } from "./routes/OverzichtPage";
import { PlanningPage } from "./routes/PlanningPage";
import { PloegenPage } from "./routes/PloegenPage";
import { WedstrijdboekjesPage } from "./routes/WedstrijdboekjesPage";
import { ReistijdenPage } from "./routes/ReistijdenPage";
import { ReistijdenKorpsenPage } from "./routes/ReistijdenKorpsenPage";
import { ReistijdenOndersteunersPage } from "./routes/ReistijdenOndersteunersPage";
import { SeizoenenPage } from "./routes/SeizoenenPage";
import { SignInFailed } from "./routes/SignInFailed";
import { SjabloonPage } from "./routes/SjabloonPage";
import { StamgegevensPage } from "./routes/StamgegevensPage";
import { SynchronisatiesPage } from "./routes/SynchronisatiesPage";
import { TextPresetsPage } from "./routes/TextPresetsPage";
import { WedstrijddagenPage } from "./routes/WedstrijddagenPage";

/**
 * Code-based routes rather than file-based. The generated route tree would be a second
 * committed artifact with its own drift check in CI, and eighteen routes do not earn one.
 */
const rootRoute = createRootRoute({
  component: Outlet,
  notFoundComponent: NotFound,
});

/**
 * In the URL, not component state: the back gesture returns to the list, and a link survives
 * being mailed. `.catch()`, because a stale id is a page with nothing chosen, not a not-found.
 */
const gekozen = z.string().optional().catch(undefined);

/**
 * A filter value, which is a string but need not look like one: the router parses `?antwoord=0`
 * into the number 0, and a bare `z.string()` would reject it, fall to `.catch`, and read as a
 * filter nobody set - quietly turning a cleared default back on.
 */
const filter = z
  .union([z.string(), z.number(), z.boolean()])
  .transform(String)
  .optional()
  .catch(undefined);

/**
 * In the URL for the same reason `gekozen` is, but its own field: a stale value here is page 1,
 * not a page with nothing chosen.
 */
const pagina = z.coerce.number().int().min(1).optional().catch(undefined);

/**
 * A stale value is the first tab, never an empty pane; the pane falls back, since only it knows
 * which tab is first. Rule A panes carry a slug, rule B panes the record id (ADR-0064).
 */
const tab = z.string().optional().catch(undefined);

// `periode` rides beside `korps` rather than in `PloegenDetail`'s own state (#198): the
// korpsenlijst's filter needs it too, and a selection two panes share belongs in the URL.
const ploegenSearch = z.object({
  korps: gekozen,
  periode: gekozen,
  pagina,
  reedsIngeschreven: filter,
});
// The zoekterm rides along: without it a link to a persoon reloads into a detail pane beside
// an empty list, with the ondersteuner's name nowhere on the screen. `reedsIngeschreven` is the
// ploegen screen's own filter, mirrored on the persoon axis.
const ondersteunersSearch = z.object({
  persoon: gekozen,
  q: gekozen,
  reedsIngeschreven: filter,
});
/**
 * `filter` picks which slice of the accounts list is on screen. In the URL rather than in state so
 * that a beheerder can send "the three accounts waiting on a koppeling" to a colleague, which is
 * the reason the old `/koppelingen` screen was linkable at all. An unknown value is the whole
 * list, like every other hand-typed search param here.
 */
const accountFilter = z
  .enum(["alle", "wacht", "afwijkend"])
  .optional()
  .catch(undefined);

// `deur` and `status` are the list's own filters (#194) - `filter` beside them, not `gekozen`,
// for the same reason the inschrijfperiodes one is: their values ("wachtwoord", "geblokkeerd")
// are ordinary column values, but the shape a filter descriptor's value always takes is `filter`.
const lokaleAccountsSearch = z.object({
  account: gekozen,
  filter: accountFilter,
  deur: filter,
  status: filter,
  pagina,
});
const seizoenenSearch = z.object({ seizoen: gekozen, pagina });
// The seizoen rides along beside the dag: the screen shows one seizoen's dagen and has no
// switcher, so a shared link without it would open on whichever seizoen happens to be running.
const wedstrijddagenSearch = z.object({
  seizoen: gekozen,
  dag: gekozen,
  pagina,
  tab,
  // The list's own filters, plus the two deelname tabs' - all kept here so they survive
  // picking another dag or tab.
  niveau: filter,
  datum: filter,
  antwoord: filter,
  klasse: filter,
  opgegeven: filter,
  functie: filter,
});
// Same reason as the wedstrijddagen: one seizoen's periodes, no switcher on the screen.
// `doelgroep` and `status` are `filter`, not `gekozen`: both look numeric/boolean ("2", "false").
const inschrijfperiodesSearch = z.object({
  seizoen: gekozen,
  periode: gekozen,
  pagina,
  doelgroep: filter,
  status: filter,
});
// The planning screen is one seizoen's dagen too, and the same reason applies: a link
// shared without the seizoen opens on whichever one happens to be running.
const planningSearch = z.object({ seizoen: gekozen, dag: gekozen, pagina });
// And the wedstrijdboekjes: one seizoen's documents, chosen from the same list-detail shape.
const wedstrijdboekjesSearch = z.object({ seizoen: gekozen, boekje: gekozen });
// Same two, for the same reason: both reistijden screens are one seizoen's dagen. `bron` and
// `punt` ride here too, one shared schema like wedstrijddagenSearch's tabs.
const reistijdenSearch = z.object({
  seizoen: gekozen,
  dag: gekozen,
  pagina,
  bron: filter,
  punt: filter,
});
// The overview chooses nothing; only the functies page has a record to name, and reference data
// hangs off no seizoen (ADR-0029), so neither carries one.
const functiesSearch = z.object({ functie: gekozen });
// The niveaus page grew a detail pane when the standaard speelschema's got a home on it
// (ADR-0039), so it names a record the same way.
const niveausSearch = z.object({ niveau: gekozen });
const speelschemasSearch = z.object({ niveau: gekozen, tab });
// The zoekterm rides along for the same reason the ondersteuners page carries one.
// The ticked functies as one comma-joined value, so a narrowed list survives a reload and can be
// mailed to a colleague - the same reason ADR-0028 gives for `persoon`. Joined rather than an
// array, because the router JSON-encodes an array and `?functie=%5B%22uuid%22%5D` is not a link
// anybody can read. The API takes the repeated form; the two are converted at the screen edge.
const bevoegdhedenSearch = z.object({
  persoon: gekozen,
  q: gekozen,
  pagina,
  functie: gekozen,
});
// Which doc page is open, not which record - a slug rather than an id, and `gekozen`'s
// `.catch()` already means a stale or hand-typed one falls back to the first page.
const handleidingSearch = z.object({ pagina: gekozen });

/**
 * `/` points at every group rather than at the first, so neither inschrijvingen screen is the
 * index by accident. No redirect: `/` changing meaning is not something one could express.
 */
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: OverzichtPage,
});

const ploegenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ploegen",
  component: PloegenPage,
  validateSearch: (search: Record<string, unknown>) =>
    ploegenSearch.parse(search),
});

const accountUnknownRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account/unknown",
  component: AccountUnknown,
});

const ondersteunersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ondersteuners",
  component: OndersteunersPage,
  validateSearch: (search: Record<string, unknown>) =>
    ondersteunersSearch.parse(search),
});

const lokaleAccountsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/lokale-accounts",
  component: LokaleAccountsPage,
  validateSearch: (search: Record<string, unknown>) =>
    lokaleAccountsSearch.parse(search),
});

/**
 * Koppelingen was a screen of its own over the same table as Accounts, so a beheerder looking at a
 * row had to change screens to reach half the actions on it. It is the `wacht` filter now.
 *
 * This one does get a redirect, unlike the paths above it: the handleiding linked it by name, and
 * a beheerder who bookmarked "the accounts I still have to sort out" should land on them rather
 * than on a dead URL. The chosen account rides along, since it is the same id either way.
 */
const koppelingenRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/koppelingen",
  validateSearch: (search: Record<string, unknown>) =>
    z.object({ account: gekozen }).parse(search),
  // Returned rather than thrown: `redirect` supports both, and a thrown non-Error is the one
  // thing eslint will not have.
  beforeLoad: ({ search }) =>
    redirect({
      to: "/lokale-accounts",
      search: { account: search.account, filter: "wacht" as const },
    }),
});

const seizoenenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/seizoenen",
  component: SeizoenenPage,
  validateSearch: (search: Record<string, unknown>) =>
    seizoenenSearch.parse(search),
});

const wedstrijddagenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/wedstrijddagen",
  component: WedstrijddagenPage,
  validateSearch: (search: Record<string, unknown>) =>
    wedstrijddagenSearch.parse(search),
});

const inschrijfperiodesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inschrijfperiodes",
  component: InschrijfperiodesPage,
  validateSearch: (search: Record<string, unknown>) =>
    inschrijfperiodesSearch.parse(search),
});

const wedstrijdboekjesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/wedstrijdboekjes",
  component: WedstrijdboekjesPage,
  validateSearch: (search: Record<string, unknown>) =>
    wedstrijdboekjesSearch.parse(search),
});

// No seizoen or boekje: the template is global (ADR-0071), unlike everything else under
// /wedstrijdboekjes.
const sjabloonRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/wedstrijdboekjes/sjabloon",
  component: SjabloonPage,
});

const planningRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/planning",
  component: PlanningPage,
  validateSearch: (search: Record<string, unknown>) =>
    planningSearch.parse(search),
});

const reistijdenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reistijden",
  component: ReistijdenPage,
});

const reistijdenKorpsenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reistijden/korpsen",
  component: ReistijdenKorpsenPage,
  validateSearch: (search: Record<string, unknown>) =>
    reistijdenSearch.parse(search),
});

const reistijdenOndersteunersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reistijden/ondersteuners",
  component: ReistijdenOndersteunersPage,
  validateSearch: (search: Record<string, unknown>) =>
    reistijdenSearch.parse(search),
});

const stamgegevensRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/stamgegevens",
  component: StamgegevensPage,
});

const functiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/stamgegevens/functies",
  component: FunctiesPage,
  validateSearch: (search: Record<string, unknown>) =>
    functiesSearch.parse(search),
});

const klassenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/stamgegevens/klassen",
  component: KlassenPage,
});

const niveausRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/stamgegevens/niveaus",
  component: NiveausPage,
  validateSearch: (search: Record<string, unknown>) =>
    niveausSearch.parse(search),
});

const speelschemasRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/stamgegevens/speelschemas",
  component: SpeelschemasPage,
  validateSearch: (search: Record<string, unknown>) =>
    speelschemasSearch.parse(search),
});

const standaardtekstenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/stamgegevens/standaardteksten",
  component: TextPresetsPage,
});

const bevoegdhedenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/bevoegdheden",
  component: BevoegdhedenPage,
  validateSearch: (search: Record<string, unknown>) =>
    bevoegdhedenSearch.parse(search),
});

const synchronisatiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/synchronisaties",
  component: SynchronisatiesPage,
});

const handleidingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/handleiding",
  component: HandleidingPage,
  validateSearch: (search: Record<string, unknown>) =>
    handleidingSearch.parse(search),
});

const signInFailedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account/signin-failed",
  component: SignInFailed,
});

/** Exported so a test can build a router over the real routes with its own history. */
export const routeTree = rootRoute.addChildren([
  indexRoute,
  ploegenRoute,
  ondersteunersRoute,
  seizoenenRoute,
  wedstrijddagenRoute,
  inschrijfperiodesRoute,
  wedstrijdboekjesRoute,
  sjabloonRoute,
  planningRoute,
  reistijdenRoute,
  reistijdenKorpsenRoute,
  reistijdenOndersteunersRoute,
  stamgegevensRoute,
  functiesRoute,
  klassenRoute,
  niveausRoute,
  speelschemasRoute,
  standaardtekstenRoute,
  bevoegdhedenRoute,
  accountUnknownRoute,
  lokaleAccountsRoute,
  koppelingenRedirectRoute,
  synchronisatiesRoute,
  handleidingRoute,
  signInFailedRoute,
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
